import {
  AGENT_MODEL_TIERS,
  AgentModelTierSchema,
  AgentToolSchema,
  type EvalCandidate,
  INVESTIGATOR_TOOL,
} from '@sbc/domain-models/agents';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const SAME_TIER = 'same';

export interface RunOptionsState {
  versionNumber: string;
  modelTier: string;
  noInvestigator: boolean;
  systemPrompt: string;
}

export const EMPTY_RUN_OPTIONS: RunOptionsState = {
  versionNumber: '',
  modelTier: SAME_TIER,
  noInvestigator: false,
  systemPrompt: '',
};

export function toCandidate(state: RunOptionsState): EvalCandidate | undefined {
  const candidate: EvalCandidate = {};
  const version = Number.parseInt(state.versionNumber, 10);
  if (Number.isInteger(version) && version > 0) {
    candidate.versionNumber = version;
  }
  const tier = AgentModelTierSchema.safeParse(state.modelTier);
  if (tier.success) {
    candidate.modelTier = tier.data;
  }
  if (state.noInvestigator) {
    candidate.removeTools = [AgentToolSchema.parse(INVESTIGATOR_TOOL)];
  }
  if (state.systemPrompt.trim()) {
    candidate.systemPrompt = state.systemPrompt;
  }
  return Object.keys(candidate).length === 0 ? undefined : candidate;
}

export function fromCandidate(candidate: EvalCandidate | null): RunOptionsState {
  return {
    versionNumber: candidate?.versionNumber ? String(candidate.versionNumber) : '',
    modelTier: candidate?.modelTier ?? SAME_TIER,
    noInvestigator: candidate?.removeTools?.some((tool) => tool === INVESTIGATOR_TOOL) ?? false,
    systemPrompt: candidate?.systemPrompt ?? '',
  };
}

export function RunOptions({
  value,
  onChange,
}: {
  value: RunOptionsState;
  onChange: (next: RunOptionsState) => void;
}) {
  return (
    <div className="space-y-3 rounded-md border p-3">
      <p className="text-sm font-medium">Candidate (optional)</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="runVersion">Agent version number</Label>
          <Input
            id="runVersion"
            type="number"
            min={1}
            placeholder="Active version"
            value={value.versionNumber}
            onChange={(event) => onChange({ ...value, versionNumber: event.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="runTier">Model tier</Label>
          <Select
            value={value.modelTier}
            onValueChange={(modelTier) => onChange({ ...value, modelTier })}
          >
            <SelectTrigger id="runTier">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SAME_TIER}>Same as the version</SelectItem>
              {AGENT_MODEL_TIERS.map((tier) => (
                <SelectItem key={tier} value={tier}>
                  {tier}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.noInvestigator}
          onChange={(event) => onChange({ ...value, noInvestigator: event.target.checked })}
        />
        Turn off the investigator ({INVESTIGATOR_TOOL})
      </label>
      <div className="space-y-2">
        <Label htmlFor="runPrompt">System prompt override</Label>
        <Textarea
          id="runPrompt"
          rows={4}
          placeholder="Leave empty to use the version's prompt"
          value={value.systemPrompt}
          onChange={(event) => onChange({ ...value, systemPrompt: event.target.value })}
        />
      </div>
    </div>
  );
}
